const BRAND_LOGO_OVERRIDES: Record<string, Partial<Record<number, string>>> = {
  honda: {
    2: 'honda-motorcycle',
  },
};

export function normalizeBrandName(brand: string): string {
  return brand
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, '-');
}

export function getBrandLogoFilename(brand: string, vehicleType?: number): string {
  const normalized = normalizeBrandName(brand);
  const override =
    vehicleType !== undefined ? BRAND_LOGO_OVERRIDES[normalized]?.[vehicleType] : undefined;

  return override ?? normalized;
}

export function getBrandLogo(brand: string, vehicleType?: number): string {
  const filename = getBrandLogoFilename(brand, vehicleType);

  try {
    return new URL(`/src/assets/brands/${filename}.png`, import.meta.url).href;
  } catch {
    try {
      return new URL(`/src/assets/brands/${filename}.jpeg`, import.meta.url).href;
    } catch {
      return '';
    }
  }
}

const transparentBrandLogos = import.meta.glob('../assets/brands-transparent/*.png', {
  eager: true,
  import: 'default',
}) as Record<string, string>;

const transparentBrandLogoByName: Record<string, string> = Object.fromEntries(
  Object.entries(transparentBrandLogos).flatMap(([path, url]) => {
    const name = path.split('/').pop()?.replace(/\.png$/, '');
    return name ? [[name, url]] : [];
  })
);

export function getTransparentBrandLogo(brand: string, vehicleType?: number): string {
  const filename = getBrandLogoFilename(brand, vehicleType);
  return transparentBrandLogoByName[filename] || getBrandLogo(brand, vehicleType);
}

export function getBrandVehicleType(
  brand: string,
  vehicles: { brand: string; type: number }[],
  selectedCategory = 'all'
): number | undefined {
  const pool =
    selectedCategory !== 'all'
      ? vehicles.filter((v) => v.brand === brand && String(v.type) === selectedCategory)
      : vehicles.filter((v) => v.brand === brand);

  if (!pool.length) return undefined;

  const types = [...new Set(pool.map((v) => v.type))];
  return types.length === 1 ? types[0] : undefined;
}

export type BrandFilterEntry = {
  key: string;
  brand: string;
  vehicleType: number;
  bindsCategory: boolean;
};

export function buildBrandFilterEntries(
  vehicles: { brand: string; type: number }[],
  selectedCategory = 'all'
): BrandFilterEntry[] {
  const pool =
    selectedCategory !== 'all'
      ? vehicles.filter((v) => String(v.type) === selectedCategory)
      : vehicles;

  const typesByBrand = new Map<string, Set<number>>();

  pool.forEach((vehicle) => {
    if (!vehicle.brand) return;
    if (!typesByBrand.has(vehicle.brand)) {
      typesByBrand.set(vehicle.brand, new Set());
    }
    typesByBrand.get(vehicle.brand)!.add(vehicle.type);
  });

  const entries: BrandFilterEntry[] = [];

  [...typesByBrand.keys()].sort().forEach((brand) => {
    const types = [...typesByBrand.get(brand)!].sort((a, b) => a - b);
    const categoryLocked = selectedCategory !== 'all';

    if (categoryLocked) {
      entries.push({
        key: brand,
        brand,
        vehicleType: parseInt(selectedCategory, 10),
        bindsCategory: false,
      });
      return;
    }

    if (types.length === 1) {
      entries.push({
        key: brand,
        brand,
        vehicleType: types[0],
        bindsCategory: false,
      });
      return;
    }

    types.forEach((type) => {
      entries.push({
        key: `${brand}__${type}`,
        brand,
        vehicleType: type,
        bindsCategory: true,
      });
    });
  });

  return entries;
}
